<template>
  <div class="container my-4">
    <TopNav>
      <NavItem
        icon="far fa-plus"
        :text="$t('PODCASTS.LINK.ADD_EPISODE')"
        @click="addEpisode"
      />
    </TopNav>
    <div v-if="isLoading" class="w-100 text-center">
      <HMSpinner class="mt-4" />
    </div>
    <div v-else-if="sortedEpisodes.length > 0">
      <h1 class="pt-3">{{ podcast.title }}</h1>
      <table class="w-100">
        <draggable
          :delay="draggableDelay"
          v-if="sortedEpisodes.length"
          v-model="sortedEpisodes"
          @end="reorderEpisodes"
          :options="{ handle: '.drag-handle' }"
          class="mt-3"
        >
          <tr class="w-100" v-for="episode in sortedEpisodes" :key="episode.id">
            <td>
              <span class="drag-handle mr-2"></span>
            </td>
            <td style="width: 70%">
              <div
                class="d-flex flex-column justify-content-start text-truncate text-nowrap"
              >
                <span class="c-coral pointer" @click="editEpisode(episode)">{{
                  episode.title
                }}</span>
                <span class="small">{{ episodeDuration(episode) }}</span>
              </div>
            </td>
            <td style="width: 30%">
              <div
                class="d-flex flex-column justify-content-start text-truncate text-nowrap"
              >
                <div
                  class="d-flex justify-content-start align-items-center flex-nowrap"
                >
                  <font-awesome-icon
                    v-if="!episode.is_converting"
                    :icon="['fas', 'circle']"
                    class="dot mr-2"
                    :class="`dot--${episodeStatusClass(episode)}`"
                  />
                  <div v-else>
                    <HMSpinner class="spinner" />
                  </div>
                  <span>{{ episodeStatusText(episode) }}</span>
                  <div v-if="episode.conversion_error" class="ml-2 pointer">
                    <span
                      class="c-dark-gray"
                      v-tooltip="{
                        content: episode.conversion_error,
                        delay: 100,
                        placement: 'top',
                        container: 'body',
                        trigger: 'click hover',
                      }"
                    >
                      <i class="fal fa-info-circle"></i>
                    </span>
                  </div>
                </div>
              </div>
              <div
                v-if="
                  !episode.published &&
                  !(episode.is_converting || episode.conversion_error)
                "
              >
                <span class="small" v-if="episode.scheduled_on">
                  <span>{{
                    $formatDate(episode.scheduled_on, 'date-time')
                  }}</span>
                  <span
                    class="c-coral ml-2 pointer"
                    @click="unscheduleEpisode(episode)"
                  >
                    <i class="far fa-times"></i>
                  </span>
                </span>
                <span
                  v-else
                  class="small c-coral pointer"
                  @click="scheduleEpisode(episode)"
                >
                  {{ $t('PODCASTS.LABELS.SCHEDULE') }}
                </span>
              </div>
            </td>
            <td>
              <div
                class="episode-buttons d-flex justify-content-between align-items-center flex-grow-0"
              >
                <span
                  class="mr-4 c-coral pointer"
                  @click="showDeleteModal(episode)"
                >
                  <i class="far fa-trash-alt"></i>
                </span>
                <span
                  class="mr-4 c-coral pointer"
                  @click="editEpisode(episode)"
                >
                  <i class="far fa-pencil-alt c-coral"></i>
                </span>
                <HMButton
                  v-if="!episode.active"
                  :disabled="
                    isPublishing ||
                    episode.is_converting ||
                    episode.conversion_error
                  "
                  color="brand"
                  @click="showPublishModal(episode)"
                >
                  {{ $t('PODCASTS.BUTTONS.PUBLISH') }}
                </HMButton>
              </div>
            </td>
          </tr>
        </draggable>
      </table>
    </div>
    <div v-else class="text-center mt-6 border rounded pb-6 pt-4">
      <HMEmpty
        image=""
        :title="$t('PODCASTS.TEXT.EMPTY_EPISODES_TITLE')"
        :description="$t('PODCASTS.TEXT.EMPTY_EPISODES_DESCRIPTION')"
      />
      <button
        @click="addEpisode"
        type="button"
        name="button"
        class="btn btn-primary"
      >
        {{ $t('PODCASTS.LINK.ADD_EPISODE') }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { errorToMessage } from '../../../../core/utils/errors';
import TopNav from '@/web/components/navigation/TopNav';
import NavItem from '@/web/components/navigation/NavItem';
import PodcastEpisodeModal from '../../../components/podcasts/PodcastEpisodeModal';
import { draggableDelay } from '@/web/utils/draggable';
import draggable from 'vuedraggable';
import { cloneDeep } from 'lodash';
import { formatObjectForBackend } from '../../../../core/api/utils/converter';
import PodcastEpisodeScheduleModal from '../../../components/podcasts/PodcastEpisodeScheduleModal';
import HMDialog from '../../../components/common/HMDialog';

export default {
  name: 'PodcastEpisodes',
  components: {
    TopNav,
    NavItem,
    draggable,
  },
  computed: {
    ...mapGetters({
      teacherStudioUrl: 'user/teacherStudioUrl',
      episodeConverted: 'websocket/episodeConverted',
    }),
  },
  data() {
    return {
      draggableDelay,
      podcast: null,
      episodes: null,
      isLoading: true,
      isPublishing: false,
      sortedEpisodes: [],
    };
  },
  methods: {
    async unscheduleEpisode(episode) {
      episode.scheduled_on = null;
      try {
        await this.updateEpisode(episode);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to unschedule this episode',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        await this.loadPodcastEpisodes();
      }
    },
    scheduleEpisode(episode) {
      this.$modal.show(PodcastEpisodeScheduleModal, {
        releaseOn: episode.scheduled_on,
        openPicker: true,
        confirmFn: async date => {
          episode.scheduled_on = date;
          try {
            await this.updateEpisode(episode);
          } catch (error) {
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Failed to schedule this episode',
              text: errorToMessage(error.response, `Request failed: ${error}`),
            });
          } finally {
            await this.loadPodcastEpisodes();
          }
        },
      });
    },
    episodeDuration(episode) {
      const duration_sec = episode.media.media.audio_length;
      if (!duration_sec) return '';
      if (duration_sec < 60) {
        return this.$tc('GENERAL.LABEL.SECOND_VARIANT', duration_sec, {
          count: duration_sec,
        });
      } else {
        const duration_minutes = Math.round(duration_sec / 60);
        return this.$tc('GENERAL.LABEL.MINUTE_VARIANT', duration_minutes, {
          count: duration_minutes,
        });
      }
    },
    addEpisode() {
      this.$modal.show(PodcastEpisodeModal, {
        episode: {
          explicit: false,
        },
        confirm: async episode => {
          this.isLoading = true;
          try {
            await this.$store.dispatch('podcasts/addPodcastEpisode', {
              podcastId: this.podcast.id,
              episode: formatObjectForBackend(cloneDeep(episode)),
            });
            await this.loadPodcastEpisodes();
          } catch (error) {
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Failed to add a new episode',
              text: errorToMessage(error.response, `Request failed: ${error}`),
            });
          } finally {
            this.isLoading = false;
          }
        },
      });
    },
    editEpisode(episode) {
      this.$modal.show(PodcastEpisodeModal, {
        episode,
        confirm: async episode => {
          await this.updateEpisode(episode);
        },
      });
    },
    async updateEpisode(episode) {
      this.isLoading = true;
      try {
        await this.$store.dispatch('podcasts/updatePodcastEpisode', {
          podcastId: this.podcast.id,
          episode: formatObjectForBackend(cloneDeep(episode)),
        });
        await this.loadPodcastEpisodes();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to update this episode',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async publishEpisode(episode) {
      this.isPublishing = true;
      try {
        await this.$store.dispatch('podcasts/publishPodcastEpisode', {
          podcastId: this.podcast.id,
          episodeId: episode.id,
        });
        await this.loadPodcastEpisodes();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to publish this episode',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.isPublishing = false;
      }
    },
    async showPublishModal(episode) {
      this.$modal.show(HMDialog, {
        title: this.$t('PODCASTS.TITLES.PUBLISH_EPISODE'),
        text: this.$t('PODCASTS.TEXT.CONFIRM_PUBLISH_EPISODE', {
          title: episode.title,
        }),
        confirmButton: this.$t('PODCASTS.BUTTONS.PUBLISH'),
        confirm: async () => {
          await this.publishEpisode(episode);
        },
      });
    },
    async showDeleteModal(episode) {
      this.$modal.show(HMDialog, {
        title: this.$t('PODCASTS.TITLES.DELETE_EPISODE'),
        text: this.$t('PODCASTS.TEXT.CONFIRM_DELETE_EPISODE', {
          title: episode.title,
        }),
        confirmButton: this.$t('GENERAL.BUTTON.DELETE'),
        confirm: async () => {
          await this.deleteEpisode(episode);
        },
      });
    },
    async deleteEpisode(episode) {
      try {
        this.inProcess = true;
        await this.$store.dispatch('podcasts/deletePodcastEpisode', {
          podcastId: this.podcast.id,
          episodeId: episode.id,
        });

        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('PODCASTS.NOTIFICATIONS.EPISODE_SUCCESSFULLY_DELETED'),
        });
        this.loadPodcastEpisodes();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to delete this episode',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    episodeStatusClass(episode) {
      if (episode.is_converting) return 'converting';
      if (episode.conversion_error) return 'error';
      if (episode.published) return 'published';
      if (episode.scheduled_on) return 'scheduled';
      return 'draft';
    },
    episodeStatusText(episode) {
      if (episode.is_converting) return this.$t('PODCASTS.LABELS.CONVERTING');
      if (episode.conversion_error) return this.$t('PODCASTS.LABELS.ERROR');
      if (episode.published) return this.$t('PODCASTS.LABELS.PUBLISHED');
      if (episode.scheduled_on) return this.$t('PODCASTS.LABELS.SCHEDULED');
      return this.$t('PODCASTS.LABELS.DRAFT');
    },
    async loadPodcast() {
      this.isLoading = true;
      try {
        this.podcast = await this.$store.dispatch(
          'podcasts/getPodcast',
          this.$route.params.id
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load podcast',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadPodcastEpisodes() {
      this.isLoading = true;
      try {
        this.episodes = await this.$store.dispatch(
          'podcasts/getPodcastEpisodes',
          this.$route.params.id
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load podcast episodes',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.isLoading = false;
      }
    },
    sortPositions(items) {
      if (!items) return;
      this.sortedEpisodes = items.sort((a, b) => b.position - a.position);
    },
    async reorderEpisodes() {
      const totalEpisodes = this.sortedEpisodes?.length || 0;
      const episodes = this.sortedEpisodes.map((episode, index) => {
        return {
          id: episode.id,
          position: totalEpisodes - index,
        };
      });
      await this.$store.dispatch('podcasts/reorderEpisodes', {
        podcastId: this.podcast.id,
        episodes,
      });
    },
  },
  async mounted() {
    await this.loadPodcast();
    await this.loadPodcastEpisodes();
  },
  watch: {
    episodes: {
      handler(value) {
        this.sortPositions([...cloneDeep(this.episodes)]);
      },
      deep: true,
    },
    episodeConverted: {
      handler: function (episodeConverted) {
        console.log('episodeConverted', episodeConverted);
        if (!episodeConverted) return;
        if (this.teacherStudioUrl === episodeConverted.studio_url) {
          this.loadPodcastEpisodes();
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.drag-handle {
  &:before {
    content: '☰';
    cursor: move;
    margin-right: 10px;
  }
}

.dot {
  font-size: 0.5rem;
  &--draft {
    color: $medium-gray;
  }
  &--scheduled {
    color: $mustard;
  }
  &--published {
    color: $light-green;
  }
  &--error {
    color: $coral;
  }
}

.spinner {
  height: 10px;
  width: 10px;
  margin-right: 0.5rem;
}
</style>
